import { Button, Heading, TextInput, toast } from "@bfc-software/pallet";
import { useCallback, useState } from "react";
import { Form as ReactAriaForm } from "react-aria-components";
import {
  TRANSLATION_NAMESPACE,
  useTranslation,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import { getEnv as getAppEnv } from "@bfc-software/app-toolbox";

import { LoginForm } from "../-utilities/login-form-state";

import LoginCard from "./login-card";
import styles from "./email-entry.module.css";

interface EmailEntryProps {
  lang: string;
  setParentForm: (newValues: Partial<LoginForm>) => void;
  error?: string;
  showPasswordLogin?: boolean;
}

interface EmailForm {
  email: string;
}

// Note: The "Redirect URLs" here are configured through the stytch admin web ui;
// it is an allowlist of urls that we can have them send the user _back to_ after
// authenticating.
//
// Calling that out here as the localization slug (`lang`) here is dynamic.
//
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getMicrosoftSSO = (lang: string) => {
  const publicToken = getAppEnv().publicToken;

  // Build the redirect URL
  const baseRedirect = `${getAppEnv().clientBase}/app/auth/redirect?appid=bfcweb`;

  const redirectParams = new URLSearchParams();
  redirectParams.append("discovery_redirect_url", baseRedirect);
  redirectParams.append("public_token", publicToken);

  // Build the Stytch discovery URL
  const redirectUrl =
    "https://api.stytch.com/v1/b2b/public/oauth/microsoft/discovery/start?" +
    redirectParams.toString();

  return redirectUrl;
};

function goToSSO(lang: string) {
  window.location.href = getMicrosoftSSO(lang);
}

export default function EmailEntry({
  setParentForm,
  showPasswordLogin = false,
  lang,
  error,
}: EmailEntryProps) {
  const [emailSent, setEmailSent] = useState(false);
  const [form, setForm] = useState<EmailForm>({
    email: "",
  });

  const { t } = useTranslation({
    lang: lang,
    namespace: TRANSLATION_NAMESPACE.login,
  });

  const validateEmail = (value: string) => {
    if (!value) return null;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value) ? null : t("invalidEmail");
  };

  const { mutate, isPending } = apiClient().useMutation(
    "post",
    "/auth/send-link",
  );

  const sendEmail = useCallback(async () => {
    mutate(
      {
        body: {
          email_addr: form.email,
        },
      },
      {
        onSuccess: () => {
          toast.success(t("emailEntry.emailSentSuccessfully"));
          setEmailSent(true);
        },
        onError: (error) => {
          if (error) {
            toast.error(
              <div className={styles.errorToast}>
                <p className={styles.heading}>
                  {t("emailEntry.emailSentError")}
                </p>
                <p className={styles.errorMessage}>{error.message}</p>
              </div>,
            );
          }
        },
      },
    );
  }, [mutate, form, t]);

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();

    const submitter = (ev.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement;
    const method = submitter?.dataset?.method;
    if (
      showPasswordLogin &&
      (method === "password" || method == "set-password")
    ) {
      setParentForm({
        email: form.email,
        flow: method == "set-password" ? "reset-password" : "login",
      });
    } else if (method == "magic-link") {
      sendEmail();
    }
  }

  let contentBody = (
    <ReactAriaForm onSubmit={handleSubmit} className={styles.form}>
      <TextInput
        id="email-input"
        className={styles.emailInput}
        type="text"
        label={t("username")}
        name="email"
        autofocus={true}
        onChange={(ev) => setForm({ ...form, email: ev.target.value })}
        isRequired={true}
        errorMessage={error}
        validate={validateEmail}
      />
      <div className={styles.signInOptions}>
        <div className={styles.submitButton}>
          <Button
            isDisabled={isPending}
            data-method="magic-link"
            text={t("emailEntry.magicLinkButton")}
            type="submit"
          />
        </div>
        {showPasswordLogin && (
          <div className={styles.submitButton}>
            <Button
              id="password-login-submit"
              isDisabled={isPending}
              data-method="password"
              variant="tertiary"
              text={t("emailEntry.passwordButton")}
              type="submit"
            />
          </div>
        )}
        <div className={styles.submitButton}>
          <Button
            id="set-sso-submit"
            isDisabled={isPending}
            variant="tertiary"
            text={t("emailEntry.ssoButton")}
            onPress={() => goToSSO(lang)}
          />
        </div>
        <div className={styles.submitButton}>
          <Button
            id="set-password-submit"
            isDisabled={isPending}
            data-method="set-password"
            variant="tertiary"
            text={t("emailEntry.setPasswordButton")}
            type="submit"
          />
        </div>
      </div>
    </ReactAriaForm>
  );

  if (emailSent) {
    contentBody = (
      <>
        <div className={styles.successMessage}>
          {t("emailEntry.checkEmail")}
        </div>
        <Button
          icon={{ type: "arrow-left" }}
          onPress={() => {
            setForm({ email: "" });
            setEmailSent(false);
          }}
          variant="tertiary"
          text={t("emailEntry.signInADifferentWay")}
        />
      </>
    );
  }

  return (
    <LoginCard data-testid="email-entry">
      <Heading text={t("emailEntry.title")} level="1" />
      {contentBody}
    </LoginCard>
  );
}
